import { useState, useEffect } from "react";
import API, { formatApiError } from "@/lib/api";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHOD_LABEL = {
  bank_transfer: "Bank transfer", paypal: "PayPal", wire: "Wire", payoneer: "Payoneer",
  wise: "Wise", crypto: "Crypto", other: "Other",
};

export default function PubPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/publisher/payments");
        setData(data);
      } catch (e) {
        setError(formatApiError(e) || "Could not load payments");
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">{error}</div>;

  const payments = data?.payments || [];
  const share = data?.pub_share_pct ?? 100;
  const hasCut = share < 100;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold">Payments</h1>
        <p className="text-sm text-muted-foreground">
          {hasCut
            ? <>Your revenue share is <span className="font-semibold">{share}%</span>. Payable is your {share}% share of approved commission.</>
            : <>Payable is your approved commission, confirmed by the networks.</>}
        </p>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${hasCut ? "sm:grid-cols-4" : "sm:grid-cols-3"}`}>
        {hasCut && (
          <Card label="Approved commission" value={money(data?.gross_approved_usd)}
                hint="Confirmed by the networks" />
        )}
        <Card label={hasCut ? `Your ${share}% share` : "Approved earnings"} value={money(data?.payable_usd)}
              hint={hasCut ? `After the ${(100 - share)}% platform fee (${money(data?.platform_fee_usd)})` : "Confirmed by the networks"} />
        <Card label="Paid to you" value={money(data?.total_paid_usd)}
              hint={`${data?.payment_count || 0} payment${(data?.payment_count || 0) === 1 ? "" : "s"}`} />
        <Card label="Outstanding balance" value={money(data?.outstanding_balance_usd)}
              hint="Payable − paid" highlight />
      </div>

      <div className="rounded-lg border">
        <div className="px-4 py-3 border-b font-semibold text-sm">Payment history</div>
        {payments.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No payments recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Method</th>
                  <th className="px-4 py-2 font-medium">Reference</th>
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id} className="border-b last:border-0">
                    <td className="px-4 py-2 whitespace-nowrap">{p.paid_on}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium">{money(p.amount_usd)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{METHOD_LABEL[p.method] || p.method}</td>
                    <td className="px-4 py-2">{p.reference || "—"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {p.period_start || p.period_end ? `${p.period_start || "…"} → ${p.period_end || "…"}` : "—"}
                    </td>
                    <td className="px-4 py-2">{p.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, hint, highlight }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "bg-primary/5 border-primary/30" : ""}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
